import { mediaBlobId, type MediaBlobId } from "./MediaBlob";
import { organizationsId, type OrganizationsId } from "./Organizations";
import { projectsId, type ProjectsId } from "./Projects";
import { usersId, type UsersId } from "./Users";
import type { ColumnType, Selectable, Insertable, Updateable } from "kysely";
import { z } from "zod";

/** Identifier type for media_item */
export type MediaItemId = string & { __flavor: "MediaItemId" };

/**
 * Represents the table public.media_item
 * @uniqueConstraints {"media_item_pkey":{"name":"media_item_pkey","isPrimary":true,"isUnique":true,"columns":[{"name":"id","definition":"id","predicate":null}]},"media_item_org_project_mutual_exclusive":{"name":"media_item_org_project_mutual_exclusive","isPrimary":false,"isUnique":true,"columns":[{"name":"organization_id","definition":"organization_id","predicate":null},{"name":"project_id","definition":"project_id","predicate":null}]},"media_item_blob_org_unique":{"name":"media_item_blob_org_unique","isPrimary":false,"isUnique":true,"columns":[{"name":"blob_id","definition":"blob_id","predicate":null},{"name":"organization_id","definition":"organization_id","predicate":null}]},"media_item_blob_project_unique":{"name":"media_item_blob_project_unique","isPrimary":false,"isUnique":true,"columns":[{"name":"blob_id","definition":"blob_id","predicate":null},{"name":"project_id","definition":"project_id","predicate":null}]},"idx_media_item_organization":{"name":"idx_media_item_organization","isPrimary":false,"isUnique":false,"columns":[{"name":"organization_id","definition":"organization_id","predicate":null}]},"idx_media_item_project":{"name":"idx_media_item_project","isPrimary":false,"isUnique":false,"columns":[{"name":"project_id","definition":"project_id","predicate":null}]},"idx_media_item_blob":{"name":"idx_media_item_blob","isPrimary":false,"isUnique":false,"columns":[{"name":"blob_id","definition":"blob_id","predicate":null}]},"idx_media_item_created_at":{"name":"idx_media_item_created_at","isPrimary":false,"isUnique":false,"columns":[{"name":"created_at","definition":"created_at","predicate":null}]},"idx_media_item_created_by":{"name":"idx_media_item_created_by","isPrimary":false,"isUnique":false,"columns":[{"name":"created_by","definition":"created_by","predicate":null}]},"idx_media_item_name":{"name":"idx_media_item_name","isPrimary":false,"isUnique":false,"columns":[{"name":"name","definition":"name","predicate":null}]}}
 */
export default interface MediaItemTable {
  id: ColumnType<MediaItemId, MediaItemId | undefined, MediaItemId>;

  blobId: ColumnType<MediaBlobId, MediaBlobId | undefined, MediaBlobId>;

  organizationId: ColumnType<
    OrganizationsId | null,
    OrganizationsId | null,
    OrganizationsId | null
  >;

  projectId: ColumnType<ProjectsId | null, ProjectsId | null, ProjectsId | null>;

  name: ColumnType<string, string, string>;

  tags: ColumnType<string[], string[] | undefined, string[]>;

  alt: ColumnType<string | null, string | null, string | null>;

  description: ColumnType<string | null, string | null, string | null>;

  metadata: ColumnType<unknown | null, unknown | null, unknown | null>;

  createdAt: ColumnType<Date, Date | string | undefined, Date | string>;

  updatedAt: ColumnType<Date, Date | string | undefined, Date | string>;

  createdBy: ColumnType<UsersId, UsersId, UsersId>;

  updatedBy: ColumnType<UsersId, UsersId, UsersId>;
}

export type MediaItem = Selectable<MediaItemTable>;

export type NewMediaItem = Insertable<MediaItemTable>;

export type MediaItemUpdate = Updateable<MediaItemTable>;

export const mediaItemId = z.string() as unknown as z.Schema<MediaItemId>;

export const mediaItem = z.object({
  id: mediaItemId,
  blobId: mediaBlobId,
  organizationId: organizationsId.nullable(),
  projectId: projectsId.nullable(),
  name: z.string(),
  tags: z.string().array(),
  alt: z.string().nullable(),
  description: z.string().nullable(),
  metadata: z.unknown().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
  createdBy: usersId,
  updatedBy: usersId,
});

export const mediaItemInitializer = z.object({
  id: mediaItemId.optional(),
  blobId: mediaBlobId.optional(),
  organizationId: organizationsId.optional().nullable(),
  projectId: projectsId.optional().nullable(),
  name: z.string(),
  tags: z.string().array().optional(),
  alt: z.string().optional().nullable(),
  description: z.string().optional().nullable(),
  metadata: z.unknown().optional().nullable(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
  createdBy: usersId,
  updatedBy: usersId,
});

export const mediaItemMutator = z.object({
  id: mediaItemId.optional(),
  blobId: mediaBlobId.optional(),
  organizationId: organizationsId.optional().nullable(),
  projectId: projectsId.optional().nullable(),
  name: z.string().optional(),
  tags: z.string().array().optional(),
  alt: z.string().optional().nullable(),
  description: z.string().optional().nullable(),
  metadata: z.unknown().optional().nullable(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
  createdBy: usersId.optional(),
  updatedBy: usersId.optional(),
});

// ── constraints for MediaItem ─────────────────────────────────
export const MediaItemConstraints = {
  media_item_pkey: {
    name: "media_item_pkey",
    isPrimary: true,
    isUnique: true,
    columns: [{ name: "id", definition: "id", predicate: null }],
  },
  media_item_org_project_mutual_exclusive: {
    name: "media_item_org_project_mutual_exclusive",
    isPrimary: false,
    isUnique: true,
    columns: [
      { name: "organization_id", definition: "organization_id", predicate: null },
      { name: "project_id", definition: "project_id", predicate: null },
    ],
  },
  media_item_blob_org_unique: {
    name: "media_item_blob_org_unique",
    isPrimary: false,
    isUnique: true,
    columns: [
      { name: "blob_id", definition: "blob_id", predicate: null },
      { name: "organization_id", definition: "organization_id", predicate: null },
    ],
  },
  media_item_blob_project_unique: {
    name: "media_item_blob_project_unique",
    isPrimary: false,
    isUnique: true,
    columns: [
      { name: "blob_id", definition: "blob_id", predicate: null },
      { name: "project_id", definition: "project_id", predicate: null },
    ],
  },
  idx_media_item_organization: {
    name: "idx_media_item_organization",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "organization_id", definition: "organization_id", predicate: null }],
  },
  idx_media_item_project: {
    name: "idx_media_item_project",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "project_id", definition: "project_id", predicate: null }],
  },
  idx_media_item_blob: {
    name: "idx_media_item_blob",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "blob_id", definition: "blob_id", predicate: null }],
  },
  idx_media_item_created_at: {
    name: "idx_media_item_created_at",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "created_at", definition: "created_at", predicate: null }],
  },
  idx_media_item_created_by: {
    name: "idx_media_item_created_by",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "created_by", definition: "created_by", predicate: null }],
  },
  idx_media_item_name: {
    name: "idx_media_item_name",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "name", definition: "name", predicate: null }],
  },
} as const;
export type MediaItemConstraintsType = typeof MediaItemConstraints;
